import * as React from 'react';
import { FC, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Alert, Button, ButtonsContainer, typeCompact14Regular, Window, WindowBody } from '@traceair/tds';
import { classes, style } from 'typestyle';
import { trackAction } from '../../../../../../libs/analytics/ActionTracking';
import FlightDatePicker from '../../flightUpload/FlightDatePicker';
import { GcpDateModalReason } from './resolveGcpServiceDate';
import { GcpServiceDateCancelledError } from './GcpServiceDateCancelledError';

type ConfirmGcpServiceDateProps = {
  showWindow: (window: React.ReactNode) => void;
  hideWindow: () => void;
  /** Date the picker opens on (YYYY-MM-DD) — the measurement file's lastModified day. */
  defaultDate: string;
  /** Latest selectable day (YYYY-MM-DD), i.e. today in the site's timezone. */
  maxDate: string;
  reason: GcpDateModalReason;
  siteName: string;
};

const bodyStyle = classes(typeCompact14Regular, style({ padding: 12, textAlign: 'left' }));
const pickerStyle = style({ padding: '0 12px 12px', width: 220, textAlign: 'left' });

const message = (reason: GcpDateModalReason, defaultDate: string): string => {
  if (reason === 'suspicious-age') {
    return (
      `This measurement file was last changed on ${format(parseISO(defaultDate), 'd LLL yyyy')}, ` +
      `over a week ago. If that isn't the day you measured the GCPs, please set the correct one — ` +
      `it's important that we have the real date.`
    );
  }
  return (
    "We couldn't read a measurement date from this file. Please confirm the day the GCPs were actually " +
    "measured — it's important that we have the real date."
  );
};

/**
 * Asks the pilot to confirm the day the GCPs were measured, when it could not be read from the
 * measurement file. Resolves with the chosen date (YYYY-MM-DD); rejects with
 * {@link GcpServiceDateCancelledError} if they back out, in which case nothing is submitted.
 */
export default function confirmGcpServiceDate(props: ConfirmGcpServiceDateProps): Promise<string> {
  const { showWindow, hideWindow, defaultDate, maxDate, reason, siteName } = props;

  return new Promise((resolve, reject) => {
    const handleCancel = () => {
      hideWindow();
      reject(new GcpServiceDateCancelledError());
    };

    const handleConfirm = (date: string) => {
      trackAction('Confirm GCP service date', null);
      hideWindow();
      resolve(date);
    };

    const modal = (
      <Window title="When were the GCPs measured?" onClose={handleCancel} subTitle={siteName}>
        <ConfirmGcpDateBody
          message={message(reason, defaultDate)}
          defaultDate={defaultDate}
          maxDate={maxDate}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        />
      </Window>
    );

    showWindow(modal);
  });
}

type ConfirmGcpDateBodyProps = {
  message: string;
  defaultDate: string;
  maxDate: string;
  onCancel: () => void;
  onConfirm: (date: string) => void;
};

const ConfirmGcpDateBody: FC<ConfirmGcpDateBodyProps> = ({ message, defaultDate, maxDate, onCancel, onConfirm }) => {
  const [date, setDate] = useState<Date>(parseISO(defaultDate));

  return (
    <WindowBody>
      <div className={bodyStyle}>
        <Alert type="attention" title={message} />
      </div>
      <div className={pickerStyle}>
        <FlightDatePicker
          date={date}
          maxDate={parseISO(maxDate)}
          onChange={(d) => d && setDate(d)}
          placeholder="Measurement date"
        />
      </div>
      <ButtonsContainer>
        <Button style="secondary" onClick={onCancel} text="Cancel" />
        <Button
          style="primary"
          onClick={() => onConfirm(format(date, 'yyyy-MM-dd'))}
          text="Confirm"
          className="confirm-button"
        />
      </ButtonsContainer>
    </WindowBody>
  );
};
